"use client";

import type { CartState } from "@/lib/cart";
import { products } from "@/lib/products";

// ─── Closed PO summary types ───────────────────────────────────────────────────

export type ClosedPOVariant = {
  variantId: string;
  variantName: string;
  /** Total qty yang belum disubmit ke produksi (item_status = 'Menunggu') */
  pendingQty: number;
  /** Jumlah pelanggan/reseller unik yang masih menunggu */
  resellerCount: number;
  /** Qty yang sudah disubmit ke produksi (akumulatif) */
  submittedQty: number;
  productionStatus: "pending" | "partial" | "submitted";
  submittedAt: string | null;
};

export type ClosedPOProduct = {
  productId: string;
  productName: string;
  brand: string;
  /** Kapan PO benar-benar ditutup (aktual, dari products.closed_at) */
  closedAt: string | null;
  /** Tanggal deadline PO yang direncanakan */
  deadline: string | null;
  estimatedReady: string;
  /** True jika minimal satu varian sudah pernah disubmit */
  hasAnySubmitted: boolean;
  /** True jika semua varian aktif sudah berstatus 'submitted' */
  allSubmitted: boolean;
  /** Total pcs dipesan pada PO ini (Σ pending + submitted per varian). */
  totalPcs?: number;
  /** Jumlah pembeli/reseller unik pada PO ini. */
  totalBuyers?: number;
  /** Cover image produk (product_media). null bila belum ada gambar. */
  imageUrl?: string | null;
  variants: ClosedPOVariant[];
};

export type CustomerLevel = "Customer" | "Reseller" | "Agen";
export type CustomerOrderStatus =
  | "Menunggu"
  | "Diproses"
  | "Produksi"
  | "Belum Ready"
  | "Ready"
  | "Dikirim";
export type DistributionStatus = "Menunggu" | "Dikirim";

export type LocalOrder = {
  id: string;
  remoteId?: string;
  productId?: string;
  productSlug?: string;
  productName: string;
  productImage: string;
  /** Gudang produk SAAT INI (Multigudang, mig 7200; `null` = Gudang Utama) — badge kartu Belum Ready. */
  warehouseId?: string | null;
  productStatus?: string;
  /** Produk Non Web (`products.is_manual`, mig 121) — teks Belum Ready khusus (bughunt D8-18). */
  isManual?: boolean;
  preorderDeadline?: string;
  productNote?: string;
  customerName: string;
  phone: string;
  address?: string;
  note?: string;
  status: CustomerOrderStatus;
  distributionStatus: DistributionStatus;
  estimatedReady: string;
  price?: number;
  totalQty: number;
  totalPrice: number | null;
  createdAt: string;
  lines: CartState["lines"];
  availableVariations?: CartState["lines"];
  /**
   * Ada baris hidup ber-voucher (mig 5610). Sejak mig 6460 pembeli tetap bisa
   * Edit Pesanan: server menghitung ulang voucher yang sama atas isi baru, atau
   * menolak edit bila vouchernya tak lagi berlaku. Absen = tidak diketahui.
   */
  hasVoucher?: boolean;
  /** Nama voucher pada baris hidup (catatan di modal Edit Pesanan). */
  voucherName?: string;
  /**
   * Ada baris yang akan ditulis ulang Edit Pesanan (keep NULL) dengan harga
   * yang diatur admin — Harga Jual baris / diskon manual keranjang admin
   * (mig 7060). Server menolak tulis-ulangnya (gerbang HARGA_MANUAL), jadi
   * tombol Edit Pesanan diganti keterangan. Absen = tidak diketahui (tamu).
   */
  hasManualPrice?: boolean;
  /**
   * Ada baris BUNDEL hidup (mig 8040). Server menolak tulis-ulang Edit Pesanan
   * (gerbang BUNDEL), jadi tombolnya diganti keterangan + Batalkan Bundel di
   * Keranjang Pesanan. Absen = tidak diketahui (tamu).
   */
  hasBundle?: boolean;
  /**
   * Baris yang dimuat modal Edit Pesanan — HANYA yang ditulis ulang RPC
   * (keep_status NULL; lihat `orderEditLinesFromItems`). Absen (pesanan tamu
   * localStorage) = pakai `lines`.
   */
  editLines?: CartState["lines"];
};

const ORDER_KEY = "galea-preorder-orders";

export function getLocalOrders(): LocalOrder[] {
  if (typeof window === "undefined") {
    return [];
  }

  const savedOrders = window.localStorage.getItem(ORDER_KEY);
  if (!savedOrders) {
    return [];
  }

  try {
    const parsedOrders = JSON.parse(savedOrders) as Array<Partial<LocalOrder>>;
    return parsedOrders.map(normalizeOrder);
  } catch {
    return [];
  }
}

function normalizeOrder(order: Partial<LocalOrder>): LocalOrder {
  const lines = order.lines ?? [];
  const totalQty =
    order.totalQty ?? lines.reduce((sum, line) => sum + line.qty, 0);
  const lineTotal = lines.reduce(
    (sum, line) =>
      typeof line.unitPrice === "number" ? sum + line.unitPrice * line.qty : sum,
    0
  );
  const allLinesHavePrices = lines.every(
    (line) => typeof line.unitPrice === "number"
  );
  const product = products.find(
    (currentProduct) =>
      currentProduct.id === order.productId ||
      currentProduct.name === order.productName
  );
  const productPrice =
    order.price ??
    (typeof order.totalPrice === "number" && totalQty > 0
      ? Math.round(order.totalPrice / totalQty)
      : undefined);

  return {
    id: order.id ?? `PO-${Date.now().toString().slice(-7)}`,
    remoteId: order.remoteId,
    productId: order.productId ?? product?.id,
    productSlug: order.productSlug ?? product?.slug,
    productName: order.productName ?? "Produk",
    productImage: order.productImage ?? "",
    productStatus: order.productStatus ?? product?.status,
    preorderDeadline: order.preorderDeadline ?? product?.deadline,
    productNote: order.productNote?.slice(0, 10),
    customerName: order.customerName ?? "",
    phone: order.phone ?? "",
    address: order.address,
    note: order.note,
    status: normalizeOrderStatus(order.status),
    distributionStatus:
      order.distributionStatus ?? (order.status === "Dikirim" ? "Dikirim" : "Menunggu"),
    estimatedReady: order.estimatedReady ?? "sesuai update admin",
    price: productPrice,
    totalQty,
    totalPrice:
      typeof order.totalPrice === "number"
        ? order.totalPrice
        : allLinesHavePrices
          ? lineTotal
          : null,
    createdAt: order.createdAt ?? new Date().toISOString(),
    lines,
    availableVariations:
      order.availableVariations ??
      product?.variations.map((variation) => ({
        variationId: variation.id,
        variationName: variation.name,
        unitPrice: variation.price_normal,
        qty: 0
      }))
  };
}

function normalizeOrderStatus(status?: string): CustomerOrderStatus {
  switch (status) {
    case "Diproses":
    case "Produksi":
    case "Belum Ready":
    case "Ready":
    case "Dikirim":
      return status;
    case "PROSES INPUT":
      return "Diproses";
    case "PROSES PRODUKSI":
      return "Produksi";
    case "READY SEBAGIAN":
    case "READY TOKO":
    case "READY LENGKAP":
      return "Ready";
    case "PENDISTRIBUSIAN":
      return "Dikirim";
    default:
      return "Menunggu";
  }
}

export function saveLocalOrder(
  order: Omit<
    LocalOrder,
    "id" | "status" | "distributionStatus" | "createdAt"
  >
) {
  const orders = getLocalOrders();
  const nextOrder: LocalOrder = {
    ...order,
    id: `PO-${Date.now().toString().slice(-7)}`,
    status: "Menunggu",
    distributionStatus: "Menunggu",
    createdAt: new Date().toISOString()
  };

  window.localStorage.setItem(ORDER_KEY, JSON.stringify([nextOrder, ...orders]));
  return nextOrder;
}

export function updateLocalOrderLines(
  orderId: string,
  lines: CartState["lines"],
  productNote?: string
) {
  const orders = getLocalOrders();
  const nextOrders = orders.map((order) => {
    if (order.id !== orderId) {
      return order;
    }

    const totalQty = lines.reduce((sum, line) => sum + line.qty, 0);
    const lineTotal = lines.reduce(
      (sum, line) =>
        typeof line.unitPrice === "number"
          ? sum + line.unitPrice * line.qty
          : sum,
      0
    );
    const allLinesHavePrices = lines.every(
      (line) => typeof line.unitPrice === "number"
    );
    const totalPrice =
      allLinesHavePrices
        ? lineTotal
        : typeof order.price === "number"
          ? order.price * totalQty
          : order.totalPrice;

    return {
      ...order,
      productNote: productNote?.slice(0, 10),
      totalQty,
      totalPrice,
      lines
    };
  });

  window.localStorage.setItem(ORDER_KEY, JSON.stringify(nextOrders));
  return nextOrders;
}
